// 11 §6.2 — the SimBridge interface. The real implementation wraps `fk-sim-wasm`
// (crates/fk-sim-wasm/pkg, wasm-pack --target web): postcard bytes in, postcard bytes out,
// decoded by the generated decoders. Until it lands, `MockSimBridge` (./MockSimBridge.ts)
// answers from the 04 §12 fixture. Decision C5: main thread, synchronous.
import type { Intent, LegalityVerdict, ProjectedBoard, ResolutionLog, Snapshot } from './types';

/** Opaque handle to a snapshot decoded once and kept in wasm memory (or, mocked, in JS). */
export interface SnapshotHandle { readonly id: number; readonly hash: string }

export interface RealmIntent { kind: string; payload: unknown }
export interface RealmPreview { lines: string[] }

export interface SimBridgeStats { lastProjectMs: number; p95ProjectMs: number; calls: number }

export interface SimBridge {
  readonly rulesetVersion: number;
  loadSnapshot(bytes: Uint8Array): Promise<SnapshotHandle>;
  legal(h: SnapshotHandle, intent: Intent): LegalityVerdict;               // sync, sub-ms
  project(h: SnapshotHandle, intents: Intent[]): ProjectedBoard;           // sync, budget 8 ms
  planHash(h: SnapshotHandle, intents: Intent[]): string;
  resolveForParity(h: SnapshotHandle, intents: Intent[], stream: Uint8Array): ResolutionLog; // dev builds
  realmPreview(state: Uint8Array, intents: RealmIntent[]): RealmPreview;
  stats(): SimBridgeStats;
}

/**
 * Presentation-side access to the snapshot a handle refers to. The real bridge keeps the
 * snapshot in wasm memory and the scene reads a decoded `Snapshot` view of it; the mock keeps
 * it in a Map. Both expose it through this accessor so the scene code is identical.
 */
export interface SnapshotAccess {
  snapshotFor(h: SnapshotHandle): Snapshot | undefined;
}

/** Rolling p95 helper shared by implementations for `stats()`. */
export class ProjectStats implements SimBridgeStats {
  lastProjectMs = 0;
  p95ProjectMs = 0;
  calls = 0;
  private samples: number[] = [];
  record(ms: number): void {
    this.lastProjectMs = ms;
    this.calls += 1;
    this.samples.push(ms);
    if (this.samples.length > 200) this.samples.shift();
    const sorted = [...this.samples].sort((a, b) => a - b);
    this.p95ProjectMs = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))] ?? ms;
  }
  snapshot(): SimBridgeStats {
    return { lastProjectMs: this.lastProjectMs, p95ProjectMs: this.p95ProjectMs, calls: this.calls };
  }
}
